/**
 * HomePage — the gallery overview.
 *
 * Shows:
 * - The hero (gallery name + tagline + stats)
 * - Featured modules (first 6)
 * - Featured bundles
 */

import { Link } from 'react-router-dom';
import { useGallery, useFilteredModules, useFilteredBundles } from '../state/gallery-store';
import { ModuleCard } from '../components/ModuleCard';
import { BundleCard } from '../components/BundleCard';
import './HomePage.css';

export function HomePage() {
  const { registry, stats } = useGallery();
  const allModules = useFilteredModules(); // unfiltered
  const allBundles = useFilteredBundles(); // unfiltered
  const featuredModules = allModules.slice(0, 6);
  const featuredBundles = allBundles.slice(0, 4);

  if (!registry) return null;

  return (
    <div className="home">
      <section className="home__hero">
        <h1 className="home__title">FV Module Gallery</h1>
        <p className="home__tagline">
          Constitutional modules + bundles for the FreshVibe ecosystem.
          Everything FVW v8 compliant. Every module reproducible.
        </p>

        {stats && (
          <div className="home__stats">
            <div className="home__stat">
              <span className="home__stat-value">{stats.total_modules}</span>
              <span className="home__stat-label">modules</span>
            </div>
            <div className="home__stat">
              <span className="home__stat-value">{stats.total_bundles}</span>
              <span className="home__stat-label">bundles</span>
            </div>
            <div className="home__stat">
              <span className="home__stat-value">{stats.total_facets}</span>
              <span className="home__stat-label">facets</span>
            </div>
            <div className="home__stat">
              <span className="home__stat-value">{stats.total_variants}</span>
              <span className="home__stat-label">variants</span>
            </div>
            <div className="home__stat">
              <span className="home__stat-value">
                {stats.total_chrome_modules}/{stats.total_engine_modules}
              </span>
              <span className="home__stat-label">chrome / engine</span>
            </div>
          </div>
        )}
      </section>

      {featuredBundles.length > 0 && (
        <section className="home__section">
          <header className="home__section-header">
            <h2 className="home__section-title">Bundles</h2>
            <Link to="/bundles" className="home__section-link">
              View all →
            </Link>
          </header>
          <div className="home__grid">
            {featuredBundles.map((b) => (
              <BundleCard key={b.id} bundle={b} />
            ))}
          </div>
        </section>
      )}

      {featuredModules.length > 0 && (
        <section className="home__section">
          <header className="home__section-header">
            <h2 className="home__section-title">Featured modules</h2>
            <Link to="/modules" className="home__section-link">
              View all →
            </Link>
          </header>
          <div className="home__grid home__grid--cards">
            {featuredModules.map((m) => (
              <ModuleCard key={m.id} module={m} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
